/**
 * Which Sentry `environment` bucket does this browser tab belong in?
 *
 * ⚠️ WHY THIS EXISTS, precisely.
 *
 * `Sentry.init()` used to pass `environment: import.meta.env.MODE`. That reads
 * like it separates production from everything else, and it does not: Vercel
 * builds a preview deploy with the same `vite build` as production, so `MODE`
 * is the string `"production"` on **both**. Preview events weren't merely
 * indistinguishable from real traffic — they were stamped `production` and
 * counted as it.
 *
 * That cost two triage sessions in nine days (#869):
 *   - Aug 5, 2026: the `formatTime12h` issue was 67% `*.vercel.app` preview URLs.
 *   - Aug 13, 2026: JAVASCRIPT-REACT-P showed 414 events and read as a live P0
 *     until the `url` tag turned out to be
 *     `kin-ow4dauxbg-kinyoudoitall-9787s-projects.vercel.app` — a preview deploy
 *     no user can reach.
 *
 * The only thing that actually distinguishes the two is the hostname a mom's
 * phone is pointed at, so that is what we key on. Build-time signals can't do
 * this job; the deploy that serves `app.kinyoudoitall.com` and the deploy that
 * serves a preview URL are byte-identical bundles.
 *
 * Preview events are **tagged, not dropped** — they're useful when we're
 * deliberately testing. Sentry's "All Envs" dropdown does the filtering.
 */

/** The one hostname real users reach. Everything else is ours. */
export const PRODUCTION_HOSTNAME = "app.kinyoudoitall.com";

export type SentryEnvironment = "production" | "preview" | "ci";

/**
 * Substrings that identify an automated browser by its user-agent.
 *
 * `navigator.webdriver` is the reliable signal and is checked first; these are
 * the backstop for a runner that leaves it unset. Matched case-sensitively and
 * as substrings, which is how they actually appear:
 *   "...HeadlessChrome/127.0.6533.88 Safari/537.36"
 */
const AUTOMATION_UA_MARKERS = ["HeadlessChrome", "Playwright"] as const;

/**
 * Is this tab being driven by a test runner rather than a person?
 *
 * ⚠️ Must never throw. It runs inside the `Sentry.init()` argument list, before
 * anything else in the app — an exception here means Sentry never initialises
 * and we lose ALL error reporting, which is far worse than a mistagged event.
 * Hence: `typeof` guard for a missing `navigator`, a `typeof` check on
 * `userAgent` (a stubbed or spoofed navigator can hand back a non-string), and
 * a catch for a property getter that throws. Fails **closed** to `false`, i.e.
 * to the existing hostname behaviour.
 */
function isAutomatedBrowser(): boolean {
  try {
    if (typeof navigator === "undefined" || !navigator) return false;

    if (navigator.webdriver === true) return true;

    const ua = navigator.userAgent;
    if (typeof ua !== "string") return false;

    return AUTOMATION_UA_MARKERS.some((marker) => ua.includes(marker));
  } catch {
    return false;
  }
}

/**
 * Maps a hostname to a Sentry environment.
 *
 * Deliberately two hostname buckets, not three: localhost lands in `preview`
 * alongside the Vercel preview deploys. Both mean "not a real user", which is
 * the only distinction triage cares about, and a third value would add a row to
 * the environment dropdown for no decision it changes.
 *
 * `ci` is the exception that earned its row, and it is checked FIRST — before
 * the hostname — because that is the entire point. Playwright e2e runs against
 * the real production host, so the hostname says `production` and the events
 * are indistinguishable from a mom's crash. That has burned a morning twice:
 *   - Aug 14, 2026: 38 events on ProSetupFlowModal, every one from a crashing
 *     `pro-setup-flow.spec.ts` on a branch that never merged.
 *   - Sep 1, 2026: JAVASCRIPT-REACT-13, 88 events, `browser` tag 100%
 *     HeadlessChrome — reached #bugs as a red alert.
 * Preview deploys were already separated by hostname (#869); this is
 * specifically the CI-against-production case, which no hostname can see.
 *
 * The #bugs Slack rule fires on `environment:production`, so moving these
 * events to `ci` is what actually silences them. That rule is a dashboard
 * setting and is not edited from this repo.
 *
 * Fails **closed** — anything unrecognised, empty, or unparseable is `preview`.
 * Under-reporting a genuine production error would be the worse bug, but the
 * fallback can only be hit off-production: on `app.kinyoudoitall.com` the
 * browser always hands us that exact hostname.
 *
 * @param hostname typically `window.location.hostname`
 */
export function resolveSentryEnvironment(
  hostname: string | null | undefined,
): SentryEnvironment {
  // FIRST, and deliberately independent of `hostname` — automation running
  // against the production host is still automation.
  if (isAutomatedBrowser()) return "ci";

  if (typeof hostname !== "string") return "preview";

  // Hostnames are case-insensitive, and a fully-qualified one may carry a
  // trailing dot (`app.kinyoudoitall.com.`) that browsers accept and resolve
  // to the same site.
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, "");

  return normalized === PRODUCTION_HOSTNAME ? "production" : "preview";
}
